import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Routes, Route, NavLink, Navigate } from 'react-router-dom';
import { matterApi, catalogApi, type MatterDetail, type MatterParty, type RoleTemplate, type MatterType, matterDisplayName } from '../../api';
import PartiesPanel from '../../components/matters/PartiesPanel';
import TasksPanel    from '../../components/matters/TasksPanel';
import TimePanel     from '../../components/matters/TimePanel';
import DiaryPanel    from '../../components/matters/DiaryPanel';
import EventsPanel   from '../../components/matters/EventsPanel';
import BillingPanel  from '../../components/matters/BillingPanel';
import DocumentsPanel from '../../components/matters/DocumentsPanel';
import AccessPanel   from '../../components/matters/AccessPanel';
import StagePlaybook from '../../components/matters/StagePlaybook';
import InterlocutoriesPanel from '../../components/matters/InterlocutoriesPanel';
import AddInterlocutoryModal from '../../components/matters/AddInterlocutoryModal';
import EditMatterModal from '../../components/matters/EditMatterModal';

export default function MatterDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  // Keep the ROWID as a string from the URL — Catalyst ROWIDs can exceed
  // JS safe-integer range and Number() would silently round them, causing
  // the subsequent GET /matters/:id to 404.
  const matterId = id || '';
  const [matter, setMatter] = useState<MatterDetail | null>(null);
  const [parties, setParties] = useState<MatterParty[]>([]);
  const [roles, setRoles] = useState<RoleTemplate[]>([]);
  const [matterTypes, setMatterTypes] = useState<MatterType[]>([]);
  const [error, setError] = useState<string | null>(null);
  // Bumped after stage-changing actions so child panels with their own
  // useEffect data fetches (StagePlaybook, TasksPanel, DiaryPanel, etc.)
  // remount and re-fetch — `key={refreshKey}` is the trigger.
  const [refreshKey, setRefreshKey] = useState(0);
  const [editing, setEditing] = useState(false);
  const [addingInterlocutory, setAddingInterlocutory] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [m, mts] = await Promise.all([
        matterApi.get(matterId),
        catalogApi.matterTypes(),
      ]);
      setMatter(m.matter);
      setParties(m.parties);
      setMatterTypes(mts.matter_types);
      const mt = mts.matter_types.find((t) => t.ROWID === m.matter.matter_type_id);
      if (mt) {
        const r = await catalogApi.partyRoleTemplates(mt.code, m.matter.jurisdiction_id);
        setRoles(r.roles);
      }
    } catch (err) {
      setError((err as Error).message);
    }
  }, [matterId]);

  const reload = useCallback(async () => {
    await load();
    setRefreshKey((k) => k + 1);
  }, [load]);

  useEffect(() => { load(); }, [load]);

  if (error) return <div className="card">Error: {error}</div>;
  if (!matter) return <div className="muted">Loading…</div>;

  const matterType = matterTypes.find((t) => t.ROWID === matter.matter_type_id);

  return (
    <div className="stack">
      <div className="row">
        <div className="grow">
          <div className="muted" style={{ fontSize: 12 }}>{matter.reference}</div>
          <h1 style={{ marginBottom: 4 }}>{matterDisplayName(matter)}</h1>
          <div className="muted">
            {matterType?.name || '—'}
            {matter.current_stage?.name ? ` · Stage: ${matter.current_stage.name}` : ''}
            {matter.case_number ? ` · Case no. ${matter.case_number}` : ''}
          </div>
        </div>
        <button onClick={() => setEditing(true)} disabled={busy}>Edit</button>
        {matter.status === 'open' && (
          <button
            onClick={async () => {
              try { await matterApi.advanceStage(matterId); await reload(); }
              catch (e) { alert((e as Error).message || 'Could not advance stage.'); }
            }}
            disabled={busy}
          >
            Advance stage
          </button>
        )}
        {matter.status === 'open' && !(matter as any).parent_matter_id && (
          <button
            onClick={() => setAddingInterlocutory(true)}
            disabled={busy}
            title="Open a child matter for an interlocutory application (master §10.4)"
          >
            + Interlocutory
          </button>
        )}
        {matter.status === 'open' && (
          <button onClick={() => matterApi.finalise(matterId).then(reload)} disabled={busy}>
            Finalise
          </button>
        )}
        {matter.status === 'finalised' && (
          <button onClick={() => matterApi.reopen(matterId).then(reload)} disabled={busy}>
            Reopen
          </button>
        )}
        {matter.status !== 'archived' && (
          <button
            disabled={busy}
            onClick={async () => {
              if (!confirm('Archive this matter? It will be hidden from the default list but all records are preserved.')) return;
              setBusy(true);
              try { await matterApi.archive(matterId); await reload(); }
              finally { setBusy(false); }
            }}
          >
            Archive
          </button>
        )}
        <button
          style={{ color: 'var(--color-danger)' }}
          disabled={busy}
          onClick={async () => {
            const label = matter.matter_name_override || matter.matter_name || matter.reference;
            if (!confirm(`Permanently delete "${label}" and all its tasks, parties, time entries, documents, and events? This cannot be undone.`)) return;
            if (!confirm('Last chance — this is a hard delete. Continue?')) return;
            setBusy(true);
            try {
              await matterApi.remove(matterId);
              navigate('/matters');
            } catch (e) {
              alert((e as Error).message);
              setBusy(false);
            }
          }}
        >
          Delete
        </button>
      </div>

      <nav className="row" style={{ borderBottom: '1px solid var(--color-border)' }}>
        <SubTab to="overview">Overview</SubTab>
        <SubTab to="parties">Parties ({parties.length})</SubTab>
        <SubTab to="tasks">Tasks</SubTab>
        <SubTab to="time">Time</SubTab>
        <SubTab to="diary">Diary</SubTab>
        <SubTab to="documents">Documents</SubTab>
        <SubTab to="billing">Billing</SubTab>
        <SubTab to="interlocutories">Interlocutories</SubTab>
        <SubTab to="events">Events</SubTab>
        <SubTab to="access">Access</SubTab>
      </nav>

      <Routes>
        <Route path="/" element={<Navigate to="overview" replace />} />
        <Route path="overview"  element={<Overview key={`ov-${refreshKey}`} matter={matter} matterId={matterId} onChange={reload} />} />
        <Route path="parties"   element={<PartiesPanel matterId={matterId} roles={roles} parties={parties} onChange={reload} />} />
        <Route path="tasks"     element={<TasksPanel    key={`tasks-${refreshKey}`}    matterId={matterId} />} />
        <Route path="time"      element={<TimePanel     key={`time-${refreshKey}`}     matterId={matterId} />} />
        <Route path="diary"     element={<DiaryPanel    key={`diary-${refreshKey}`}    matterId={matterId} />} />
        <Route path="documents" element={<DocumentsPanel key={`docs-${refreshKey}`}    matterId={matterId} />} />
        <Route path="billing"   element={<BillingPanel  key={`bill-${refreshKey}`}     matterId={matterId} />} />
        <Route path="interlocutories" element={
          <InterlocutoriesPanel
            key={`interloc-${refreshKey}`}
            matterId={matterId}
            parentMatterTypeCode={matterType?.code}
            mediationReferredAt={(matter as any).mediation_referred_at}
            mediationJointMinuteAt={(matter as any).mediation_joint_minute_at}
            onChange={reload}
          />
        } />
        <Route path="events"    element={<EventsPanel   key={`events-${refreshKey}`}   matterId={matterId} />} />
        <Route path="access"    element={<AccessPanel   matterId={matterId} matter={matter} onChange={reload} />} />
      </Routes>

      {editing && (
        <EditMatterModal
          matter={matter}
          onClose={() => setEditing(false)}
          onSaved={() => { setEditing(false); reload(); }}
        />
      )}

      {addingInterlocutory && (
        <AddInterlocutoryModal
          matterId={matterId}
          parentMatterTypeCode={matterType?.code}
          onClose={() => setAddingInterlocutory(false)}
          onCreated={(childId) => {
            setAddingInterlocutory(false);
            // Land the user on the child matter immediately so they can
            // start working it. The parent's Interlocutories tab will
            // pick the new row up next time it's visited.
            navigate(`/matters/${childId}`);
          }}
        />
      )}
    </div>
  );
}

function SubTab({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <NavLink to={to} style={({ isActive }) => ({
      padding: '10px 14px',
      borderBottom: `2px solid ${isActive ? 'var(--color-accent)' : 'transparent'}`,
      color: isActive ? 'var(--color-accent)' : 'var(--color-text)',
      textDecoration: 'none',
      fontWeight: isActive ? 600 : 400,
    })}>{children}</NavLink>
  );
}

function Overview({ matter, matterId, onChange }: { matter: MatterDetail; matterId: number | string; onChange: () => void }) {
  return (
    <div className="stack">
      <div className="card stack">
        <div className="row">
          <Field label="Reference"        value={matter.reference} />
          <Field label="Case number"      value={matter.case_number || '—'} />
          <Field label="Status"           value={matter.status} />
        </div>
        <div className="row">
          <Field label="Opened"           value={matter.opened_at} />
          <Field label="Jurisdiction"     value={matter.jurisdiction_id} />
          <Field label="Cost scale"       value={matter.cost_scale || '—'} />
        </div>
        <div className="row">
          <Field label="Confidentiality"   value={matter.confidentiality?.mode || 'standard'} />
        </div>

        <CourtPortalLinks matter={matter} matterId={matterId} onChange={onChange} />

        {matter.notes && <div><label>Notes</label><div>{matter.notes}</div></div>}
      </div>

      <div className="card">
        <h3 style={{ margin: 0, marginBottom: 14 }}>Procedural roadmap</h3>
        {/* `onChanged` propagates skip / inline-advance back up to Detail so the
            full matter reloads (parent's reload bumps refreshKey, which keys
            this whole Overview component and forces StagePlaybook to remount). */}
        <StagePlaybook matterId={matterId} onChanged={onChange} />
      </div>
    </div>
  );
}

// ---- Court Online + CaseLines integration links ----
// Editable inline. The "Open" buttons assemble the canonical portal URLs
// (Court Online uses the Caselines-co domain — court_online_case_id is the
// case reference shown in their listings; CaseLines bundle URLs are the
// raw bundle URL the firm copies out of the portal).
function CourtPortalLinks({ matter, matterId, onChange }: { matter: MatterDetail; matterId: number | string; onChange: () => void }) {
  const [editing, setEditing] = useState(false);
  const [coCase, setCoCase] = useState(matter.court_online_case_id || '');
  const [bundleUrl, setBundleUrl] = useState(matter.caselines_bundle_url || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const courtOnlineUrl = matter.court_online_case_id
    ? `https://www.courtonline.judiciary.org.za/cases/${encodeURIComponent(matter.court_online_case_id)}`
    : null;

  const save = async () => {
    // Cheap URL validation — paste-friendly, doesn't require a scheme.
    let url = bundleUrl.trim();
    if (url && !/^https?:\/\//i.test(url)) url = `https://${url}`;
    setSaving(true); setError(null);
    try {
      await matterApi.update(matterId, {
        court_online_case_id: coCase.trim() || undefined,
        caselines_bundle_url: url || undefined,
      });
      setBundleUrl(url);
      setEditing(false);
      onChange();
    } catch (e) {
      setError((e as Error).message);
    } finally { setSaving(false); }
  };

  return (
    <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: 12, marginTop: 4 }}>
      <div className="row" style={{ alignItems: 'center', marginBottom: 8 }}>
        <h4 style={{ margin: 0 }} className="grow">Court portals</h4>
        {!editing ? (
          <button onClick={() => setEditing(true)}>Edit links</button>
        ) : (
          <>
            <button onClick={() => { setEditing(false); setCoCase(matter.court_online_case_id || ''); setBundleUrl(matter.caselines_bundle_url || ''); setError(null); }} disabled={saving}>Cancel</button>
            <button className="primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
          </>
        )}
      </div>

      {editing ? (
        <div className="stack">
          <div>
            <label>Court Online case reference</label>
            <input
              value={coCase}
              onChange={(e) => setCoCase(e.target.value)}
              placeholder="e.g. 2024/123456"
            />
          </div>
          <div>
            <label>CaseLines bundle URL</label>
            <input
              value={bundleUrl}
              onChange={(e) => setBundleUrl(e.target.value)}
              placeholder="https://za.caselines.com/case/..."
            />
          </div>
          {error && <div style={{ color: 'var(--color-danger)' }}>{error}</div>}
        </div>
      ) : (
        <div className="row" style={{ gap: 16, flexWrap: 'wrap' }}>
          <div className="grow" style={{ minWidth: 240 }}>
            <label>Court Online case</label>
            <div className="row" style={{ alignItems: 'center', gap: 8 }}>
              <span style={{ fontFamily: matter.court_online_case_id ? 'ui-monospace, monospace' : 'inherit', fontSize: 13 }}>
                {matter.court_online_case_id || '—'}
              </span>
              {courtOnlineUrl && (
                <a href={courtOnlineUrl} target="_blank" rel="noopener noreferrer">
                  <button className="ghost" style={{ padding: '4px 10px', fontSize: 12 }}>Open ↗</button>
                </a>
              )}
            </div>
          </div>
          <div className="grow" style={{ minWidth: 240 }}>
            <label>CaseLines bundle</label>
            <div className="row" style={{ alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 13, color: 'var(--color-text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 280 }}>
                {matter.caselines_bundle_url ? new URL(matter.caselines_bundle_url).hostname : '—'}
              </span>
              {matter.caselines_bundle_url && (
                <a href={matter.caselines_bundle_url} target="_blank" rel="noopener noreferrer">
                  <button className="ghost" style={{ padding: '4px 10px', fontSize: 12 }}>Open ↗</button>
                </a>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="grow">
      <label>{label}</label>
      <div>{value}</div>
    </div>
  );
}

function Placeholder({ name }: { name: string }) {
  return <div className="card"><div className="muted">{name} — wires in Phase 1 hardening / Phase 2+.</div></div>;
}
